import { useEffect, useMemo, useState } from "react";

import DemoComponent from "V2.0/components/DemoComponent";
import { useNavigate, useOutletContext } from "react-router-dom";
import {
  ChatBubbleLeftEllipsisIcon,
  Cog8ToothIcon,
  DocumentDuplicateIcon,
  FolderIcon,
  PhoneArrowUpRightIcon,
  PhoneIcon,
  WrenchIcon,
} from "@heroicons/react/24/outline";

import {
  Dropdown,
  DropdownButton,
  DropdownMenu,
  DropdownItem,
} from "V2.0/common/dropdown";

import { WhatsappAssistantSettings } from "V2.0/HomePages/Assistants/AssistantSettings/WhatsappAssistantSettingsV2";
import { WhatsappAssistantTools } from "V2.0/HomePages/Assistants/AssistantTools/WhatsappAssistantToolsV2";
import { KnowledgeBase } from "V2.0/HomePages/Assistants/RAG/KnowledgeBase/KnowledgeBase";
import { WhatsAppCommunicationBinding } from "V2.0/HomePages/Assistants/CommunicationBinding/WhatsAppCommunicationBinding";
import AssistantsBackToHome from "V2.0/HomePages/Assistants/Assistant/common/AssistantsBackToHome";
import AssistantName from "V2.0/HomePages/Assistants/Assistant/common/AssistantName";
import AssistantIcon from "V2.0/HomePages/Assistants/Assistant/common/AssistantIcon";
import AssistantId from "V2.0/HomePages/Assistants/Assistant/common/AssistantId";
import GPTResponsePremiumModel from "V2.0/HomePages/Assistants/ModelSettings/GPTResponseModelCategorized/GPTResponsePremiumModel";
import WhatsappAssistantPrompt from "../../ModelSettings/Prompt/WhatsappAssistantPrompt";
import { getVoices } from "V2.0/HomePages/VoicesLibrary/service";
import { showErrorToast } from "V2.0/utils";
import { Transcription } from "../../TranscriptionSettings/Transcription";
import TabsWithPills from "V2.0/common/tabsWithPills";
import { TestVoice } from "../AssistantTest/TestVoice";
import { TTS } from "../../TTS/TTS";

enum TABS {
  PROMPT = "Prompt",
  TOOLS = "Tools",
  SETTINGS = "Settings",
  KNOWLEDGE = "Knowledge Base",
  PHONE_NUMBERS = "Phone Numbers",
}

const tabsMap = {
  [TABS.PROMPT]: (props) => <WhatsappAssistantPrompt {...props} />,
  [TABS.SETTINGS]: (props) => <WhatsappAssistantSettings {...props} />,
  [TABS.TOOLS]: (props) => <WhatsappAssistantTools {...props} />,
  [TABS.KNOWLEDGE]: (props) => <KnowledgeBase {...props} />,
  [TABS.PHONE_NUMBERS]: (props) => <WhatsAppCommunicationBinding {...props} />,
};

const TAB_ICON = {
  [TABS.PROMPT]: DocumentDuplicateIcon,
  [TABS.KNOWLEDGE]: FolderIcon,
  [TABS.SETTINGS]: Cog8ToothIcon,
  [TABS.TOOLS]: WrenchIcon,
  [TABS.PHONE_NUMBERS]: PhoneIcon,
};

function useConfigureTabs(configurationSections) {
  const [selectedTab, setSelectedTab] = useState(null);

  const activeTab = selectedTab
    ? selectedTab
    : configurationSections.length > 0
      ? configurationSections[0]
      : null;

  const tabs = useMemo(() => {
    if (!configurationSections.length) return [];

    return configurationSections.reduce((acc, tab) => {
      const Icon = TAB_ICON[tab];
      return [
        ...acc,
        {
          name: tab,
          href: "#",
          current: tab === activeTab,
          icon: <Icon className="size-5" />,
        },
      ];
    }, []);
  }, [configurationSections, activeTab]);

  return {
    activeTab,
    tabs,
    setActiveTab: setSelectedTab,
  };
}

export default function WhatsappAssistantVoiceMode(props) {
  const {
    workspaceId,
    name,
    assistantId,
    modelSettings,
    ttsSettings,
    transcriptionSettings,
    syncAssistant,
    assistantType,
    communicationBinding,
    timezoneSettings,
    voiceSettings,
    onDuplicate,
    company,
    locked,
    configurationSections,
  } = props;
  const { projects, activeProject } = useOutletContext<{
    projects: [];
    activeProject: object;
  }>();

  const { activeTab, tabs, setActiveTab } = useConfigureTabs(
    configurationSections,
  );
  const [chatOpen, setChatOpen] = useState<boolean>(false);
  const [callOpen, setCallOpen] = useState<boolean>(false);
  const [voices, setVoices] = useState([]);
  const navigate = useNavigate();

  const fromNumbers = useMemo(() => {
    return communicationBinding.map((binding) => {
      return binding.identifier;
    });
  }, [communicationBinding]);

  async function fetchVoices() {
    try {
      const response = await getVoices();
      if (response.status === 200) {
        setVoices(response.data.voices);
      }
    } catch (e) {
      showErrorToast(e, "Failed to fetch voices");
    }
  }

  useEffect(() => {
    if (workspaceId) {
      fetchVoices();
    }
  }, [workspaceId]);

  return (
    <>
      <div className="flex flex-col h-full w-full">
        <div className="flex justify-between p-4 items-center">
          <div className="flex gap-x-2">
            <AssistantsBackToHome />
            <AssistantIcon assistantType={assistantType} />
            <div className="flex flex-col gap-y-2">
              <AssistantName
                name={name}
                assistantId={assistantId}
                workspaceId={workspaceId}
                syncAssistant={syncAssistant}
                locked={locked}
              />
              <AssistantId assistantId={assistantId} />
            </div>
          </div>
          <div>
            <Dropdown>
              <DropdownButton>Test Assistant</DropdownButton>
              <DropdownMenu>
                <DropdownItem
                  onClick={() => {
                    setCallOpen(true);
                  }}
                >
                  <div className="flex items-center gap-x-2">
                    <PhoneArrowUpRightIcon className="size-4" /> Whatsapp Call
                    (Beta)
                  </div>
                </DropdownItem>
                <DropdownItem
                  onClick={() => {
                    setChatOpen(true);
                  }}
                >
                  <div className="flex items-center gap-x-2">
                    <ChatBubbleLeftEllipsisIcon className="size-4" /> Chat
                  </div>
                </DropdownItem>
              </DropdownMenu>
            </Dropdown>
          </div>
        </div>

        <div className="flex justify-between px-6 py-4">
          <div className="flex gap-x-2">
            <TTS
              voices={voices}
              settings={ttsSettings}
              syncAssistant={syncAssistant}
              workspaceId={workspaceId}
              assistantId={assistantId}
              locked={locked}
              showPremiumOptions={
                company?.voiceLibrarySettings?.elevenlabsVoicesEnabled
              }
            />

            <Transcription
              settings={transcriptionSettings}
              syncAssistant={syncAssistant}
              workspaceId={workspaceId}
              assistantId={assistantId}
              locked={locked}
            />
          </div>
          <GPTResponsePremiumModel
            settings={modelSettings}
            syncAssistant={syncAssistant}
            workspaceId={workspaceId}
            assistantId={assistantId}
            showPremiumOptions={
              company?.thirdPartyIntegrationVisibility?.premiumModelVisibility
            }
            enablePreimumOptions={
              company?.thirdPartyIntegrationVisibility?.premiumModelsSelectable
            }
            locked={locked}
            audioDataResidency={voiceSettings?.audioDataResidency}
          />
        </div>

        <div className="px-6 pb-2 shadow-sm">
          <TabsWithPills
            tabs={tabs}
            className=""
            onTabChange={(e, { tab, index }) => setActiveTab(tab.name)}
          />
        </div>

        {activeTab ? (
          tabsMap[activeTab]({
            ...props,
          })
        ) : (
          <div className="flex-1 flex items-center justify-center border p-4 rounded-lg m-4 mb-8">
            <p>No sections</p>
          </div>
        )}
      </div>

      {chatOpen && (
        <DemoComponent
          open={chatOpen}
          setOpen={setChatOpen}
          projects={projects}
          activeProject={activeProject}
          SidebarTitle="Chat with Assistant"
          assistantId={assistantId}
        />
      )}

      {callOpen && (
        <TestVoice
          fromNumbers={fromNumbers}
          projectId={workspaceId}
          assistantId={assistantId}
          open={callOpen}
          voiceSettings={voiceSettings}
          prompt={modelSettings.promptSettings.rawPrompt}
          assistantTimezone={timezoneSettings.timezone}
          onClose={() => setCallOpen(false)}
        />
      )}
    </>
  );
}
